import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const TranslationContext = createContext();

export const useTranslation = () => {
    const context = useContext(TranslationContext);
    if (!context) {
        throw new Error('useTranslation must be used within a TranslationProvider');
    }
    return context;
};

export const TranslationProvider = ({ children }) => {
    const [currentLanguage, setCurrentLanguage] = useState('de');
    const [translations, setTranslations] = useState({});

    const loadTranslations = useCallback(async (language) => {
        try {
            const response = await import(`../translations/${language}.json`);
            setTranslations(response.default);
        } catch (error) {
            console.error(`Failed to load translations for ${language}:`, error);
            // Fallback to German if language file is not found
            if (language !== 'de') {
                loadTranslations('de');
            }
        }
    }, []);

    useEffect(() => {
        // Load saved language from localStorage or default to German
        const savedLanguage = localStorage.getItem('language') || 'de';
        setCurrentLanguage(savedLanguage);
        loadTranslations(savedLanguage);
    }, [loadTranslations]);

    const changeLanguage = (language) => {
        setCurrentLanguage(language);
        localStorage.setItem('language', language);
        loadTranslations(language);
    };

    const t = (key, params = {}) => {
        const keys = key.split('.');
        let value = translations;

        for (const k of keys) {
            if (value && typeof value === 'object') {
                value = value[k];
            } else {
                return key; // Return key if translation not found
            }
        }

        if (typeof value === 'string') {
            // Replace parameters in translation string
            return Object.keys(params).reduce((str, param) => {
                return str.replace(new RegExp(`{{${param}}}`, 'g'), params[param]);
            }, value);
        }

        return key; // Return key if translation not found
    };

    return (
        <TranslationContext.Provider value={{ t, currentLanguage, changeLanguage }}>
            {children}
        </TranslationContext.Provider>
    );
};
